// src/proxy.ts — Local Anthropic-to-OpenAI translation proxy
// Adapted from cucoleadan/opencode-cowork-proxy (MIT)
import { createServer } from 'node:http';
import type { ServerResponse } from 'node:http';
import type { AddressInfo } from 'node:net';
import { appendFileSync, openSync, writeSync, closeSync } from 'node:fs';
import { readBody, extractApiKey, sendJson, watchClientDisconnect, clientDisconnected } from './http-utils.js';
import { formatAnthropicModelEntry, formatAnthropicModelList } from './server/models.js';
import {
  claudeCodeClientModelId,
  normalizeRouteLookupId,
  stripOneMContextSuffix,
} from './context-model-id.js';
import { reportPricingBoundaryCrossing } from './pricing-boundary.js';
import { routeUnavailableMessage } from './route-unavailable.js';
import {
  getProxyDebugLogPath,
  INFERENCE_PROGRESS_INTERVAL_MS,
  redactTraceLine,
  resetTraceLog,
  writeInferenceResponseLifecycleLog,
  writeInferenceResponseErrorLog,
  writeWebSocketDiagnosticLog,
} from './trace-log.js';
import {
  isThreadContinuation,
  relayAnthropicMessages,
  resolveOAuthRetryReplacement,
  THREAD_UNSUPPORTED_BODY,
  upstreamHoldsThreads,
  UpstreamUnreachableError,
} from './upstream-forward.js';
import {
  CLAUDE_CODE_CLI_VERSION,
  injectClaudeCodeBillingSystemLine,
  injectClaudeIdentity,
  selectBetaFlags,
} from './oauth/claude-identity.js';
import { createLanguageModel, isSdkMigratedNpm, maxToolsForNpm } from './provider-factory.js';
import { randomUUID } from 'node:crypto';
import {
  isOpenAiOAuthRoute,
  oauthServiceTier,
  translateRequest as sdkTranslateRequest,
  streamAnthropicResponse,
  generateAnthropicResponse,
  extractClaudeAgentIds,
  extractClaudeSessionId,
  sdkTranslationErrorSignature,
  silenceSdkWarnings,
} from './sdk-adapter.js';
import {
  anthropicErrorType,
  formatUpstreamError,
  isContextLengthExceededError,
  sdkUpstreamErrorDetails,
  upstreamHttpStatus,
} from './upstream-error.js';
import {
  anthropicMessagesEndpoint,
  anthropicPromptTooLongMessage,
  estimateAnthropicInputTokens,
} from './anthropic-endpoints.js';
import { withResponsesWebSocketDiagnosticContext } from './oauth/responses-websocket.js';
import { openCodeGoSessionHeaders } from './data/opencode-go-models.js';
import { resolveContextWindow } from './context-window.js';
import { listenTcpServer } from './listener-ready.js';
import type { ModelRuntimeCompatibility } from './model-runtime-compatibility.js';

type ProxyLog = (message: string | (() => string)) => void;

// Claude Code aborts a streaming response after ~180s without a single SSE byte.
// When an OpenAI model streams a large tool-call argument, the SDK delivers
// thousands of `tool-input-delta` parts that Relay must buffer (they are only
// flushed as one `input_json_delta` once the call completes so the input can be
// sanitized) — so the client sees dead air and disconnects mid-argument. While
// upstream is still actively delivering parts but no real output has been
// written, emit an Anthropic `ping` SSE event to keep the client's read-idle
// timer warm. Gated on recent upstream activity so a genuine upstream stall is
// still surfaced by the SDK idle watchdog rather than masked by pings forever.
const STREAM_KEEPALIVE_INTERVAL_MS = 20_000;
const STREAM_KEEPALIVE_PING = 'event: ping\ndata: {"type":"ping"}\n\n';
const INTERNAL_ADAPTER_KEEPALIVE_TIMEOUT_MS = 60_000;

function createTranslationLifecycle(
  logPath: string | undefined,
  requestId: string | undefined,
  claudeSessionId: string | undefined,
  modelId: string,
  provider: string,
) {
  if (!logPath || !requestId) return undefined;

  const startedAt = Date.now();
  let firstPartAt: number | undefined;
  let lastPartAt: number | undefined;
  let lastPartType: string | undefined;
  let lastOutputAt: number | undefined;
  let sdkParts = 0;
  let translatedBytes = 0;
  let translatedChunks = 0;
  let stopped = false;
  let dispatched = false;

  const write = (
    event: Parameters<typeof writeInferenceResponseLifecycleLog>[1]['event'],
    extra: Partial<Parameters<typeof writeInferenceResponseLifecycleLog>[1]> = {},
  ) => writeInferenceResponseLifecycleLog(logPath, {
    event,
    requestId,
    claudeSessionId,
    modelId,
    provider,
    route: 'translated',
    ...extra,
  });
  const snapshot = (now: number) => ({
    phase: !dispatched
      ? 'preparing_translation' as const
      : sdkParts === 0
        ? 'waiting_for_sdk' as const
        : 'translating' as const,
    durationMs: now - startedAt,
    sdkParts,
    ...(lastPartAt !== undefined ? { sdkIdleMs: now - lastPartAt } : {}),
    translatedBytes,
    translatedChunks,
    ...(lastOutputAt !== undefined ? { outputIdleMs: now - lastOutputAt } : {}),
    ...(lastPartType ? { lastPartType } : {}),
  });
  const timer = setInterval(() => {
    if (!stopped) write('translation_progress', snapshot(Date.now()));
  }, INFERENCE_PROGRESS_INTERVAL_MS);
  timer.unref();

  return {
    dispatched() {
      if (stopped || dispatched) return;
      dispatched = true;
      write('translation_dispatched', snapshot(Date.now()));
    },
    onPart(partType: string) {
      const now = Date.now();
      sdkParts += 1;
      lastPartAt = now;
      lastPartType = partType;
      if (firstPartAt === undefined) {
        firstPartAt = now;
        write('translation_started', {
          durationMs: now - startedAt,
          sdkParts,
          lastPartType,
        });
      }
    },
    onOutput(chunk: string) {
      translatedBytes += Buffer.byteLength(chunk);
      translatedChunks += 1;
      lastOutputAt = Date.now();
    },
    complete() {
      if (stopped) return;
      stopped = true;
      clearInterval(timer);
      write('translation_completed', snapshot(Date.now()));
    },
    cancel() {
      if (stopped) return;
      stopped = true;
      clearInterval(timer);
      write('translation_cancelled', snapshot(Date.now()));
    },
    fail(errorType: string, errorSignature?: string, errorCode?: string) {
      if (stopped) return;
      stopped = true;
      clearInterval(timer);
      write('translation_failed', {
        ...snapshot(Date.now()),
        errorType,
        errorSignature,
        errorCode,
      });
    },
  };
}

function appendSecureLog(logPath: string, line: string): void {
  const redacted = redactTraceLine(line);
  try {
    const fd = openSync(logPath, 'a', 0o600);
    try {
      writeSync(fd, `${new Date().toISOString()} ${redacted}\n`);
    } finally {
      closeSync(fd);
    }
  } catch {
    try {
      appendFileSync(logPath, `${new Date().toISOString()} ${redacted}\n`);
    } catch { /* ignore */ }
  }
}

function makeProxyLog(debug: boolean, logPath?: string): ProxyLog {
  if (!debug) return () => {};
  const path = logPath ?? getProxyDebugLogPath();
  resetTraceLog(path);
  return (message) => {
    const line = typeof message === 'function' ? message() : message;
    appendSecureLog(path, line);
  };
}

// ── HTTP server ─────────────────────────────────────────────────────

function anthropicError(res: ServerResponse, status: number, message: string, requestId?: string) {
  sendJson(res, status, {
    type: 'error',
    error: { type: anthropicErrorType(status), message },
    ...(requestId ? { request_id: requestId } : {}),
  });
}

export interface ProxyHandle {
  port: number;
  token: string;
  close: () => void;
}

/**
 * A single entry in a proxy catalog.
 * aliasId: the id advertised in /v1/models (must start with 'claude-' or 'anthropic-')
 * realModelId: the actual model id sent to the upstream provider
 * upstreamUrl: full chat-completions URL (openai) or base URL without /v1 (anthropic)
 * apiKey: per-route upstream key. SDK routes may intentionally be empty for
 * anonymous free providers; passthrough and Cloud Code routes still require it.
 */
export interface ProxyRoute {
  aliasId: string;
  realModelId: string;
  displayName: string;
  upstreamUrl: string;
  apiKey: string;
  modelFormat: 'anthropic' | 'openai';
  contextWindow?: number;
  /** Input size above which the provider bills the whole request at a higher rate. */
  pricingBoundary?: number;
  npm?: string;      // OpenCode api.npm — when SDK-migrated, routes via the adapter
  baseURL?: string;  // base URL for openai-compatible / openrouter SDK providers
  providerId?: string;
  authType?: 'api' | 'oauth' | 'none';
  oauthAccountId?: string;
  providerData?: Record<string, unknown>;
  /** Resolves the current OAuth token before dispatch and once more after an upstream HTTP 401. */
  refreshToken?: (rejectedAccessToken?: string) => Promise<string | null>;
  supportedParameters?: string[];
  reasoning?: boolean;
  interleavedReasoningField?: string;
  /** Backend capability: model requires the Responses-Lite request shape (x-openai-internal-codex-responses-lite). */
  useResponsesLite?: boolean;
  /** Backend capability: model must use the WebSocket Responses transport instead of HTTP. */
  preferWebSockets?: boolean;
  /** Provider-neutral per-model wire quirks. */
  compatibility?: ModelRuntimeCompatibility;
  /** Static headers sent on every upstream request (e.g. a plan/auth-tracking header a custom endpoint requires). */
  headers?: Record<string, string>;
}

/**
 * Produce a gateway-discovery-safe alias for a model id.
 * Claude Code's gateway discovery only shows ids starting with 'claude' or 'anthropic'.
 * claude-* ids are returned unchanged; everything else gets an 'anthropic-{providerId}__' prefix.
 * Uses stable provider id (slug), not display name — renaming a provider does not break aliases.
 */
export function aliasModelId(realId: string, providerId: string): string {
  if (realId.startsWith('claude-')) return realId;
  const sanitized = providerId.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return `anthropic-${sanitized}__${realId}`;
}

/** Resolve catalog alias when Claude Code or legacy registry ids differ by prefix/suffix. */
function lookupRoute(byAlias: Map<string, ProxyRoute>, id: string): ProxyRoute | undefined {
  return byAlias.get(normalizeRouteLookupId(id));
}

/** Short alias name → route id, resolvable in request bodies alongside route aliasIds. */
export interface ProxyModelAlias {
  name: string;
  /** Exact spelling retained in configuration, used only for diagnostics and blocking. */
  savedName?: string;
  /** All exact saved spellings represented by this canonical alias. Never routed. */
  sourceNames?: string[];
  routeId?: string;
  unavailableReason?: string;
}

function configuredAliasLookupNames(alias: ProxyModelAlias): string[] {
  const sourceNames = [
    alias.name,
    ...(alias.savedName === undefined ? [] : [alias.savedName]),
    ...(alias.sourceNames ?? []),
  ];
  return [...new Set(sourceNames.flatMap(name => {
    const trimmed = name.trim();
    return trimmed === name ? [name] : [name, trimmed];
  }))].map(normalizeRouteLookupId);
}

/** Multi-model proxy: routes each request by body.model to the correct upstream. */
export async function startProxyCatalog(
  routes: ProxyRoute[],
  defaultAliasId: string,
  debug = false,
  inferenceLogPath?: string,
  debugLogPath?: string,
  webSocketDiagnosticsLogPath?: string,
  modelAliases?: ProxyModelAlias[],
): Promise<ProxyHandle> {
  const proxyToken = randomUUID();
  silenceSdkWarnings();

  if (routes.length === 0) {
    throw new Error('Proxy catalog requires at least one route');
  }

  const byAlias = new Map(routes.map(r => [normalizeRouteLookupId(r.aliasId), r]));
  const configuredAliasNames = new Set(
    (modelAliases ?? []).flatMap(configuredAliasLookupNames),
  );
  const unavailableAliasReasons = new Map(
    (modelAliases ?? [])
      .filter(alias => alias.unavailableReason !== undefined)
      .flatMap(alias => configuredAliasLookupNames(alias).map(name => (
        [name, alias.unavailableReason!] as const
      ))),
  );
  for (const alias of modelAliases ?? []) {
    if (alias.routeId === undefined || alias.unavailableReason !== undefined) continue;
    const route = lookupRoute(byAlias, alias.routeId);
    const aliasId = normalizeRouteLookupId(alias.name);
    if (route && !byAlias.has(aliasId)) byAlias.set(aliasId, route);
  }
  const defaultRoute = lookupRoute(byAlias, defaultAliasId) ?? routes[0]!;

  const plog = makeProxyLog(debug, debugLogPath);

  const onRejection = (reason: unknown) => {
    plog(() => `Unhandled Rejection: ${reason instanceof Error ? reason.stack || reason.message : String(reason)}`);
  };
  const onException = (error: Error) => {
    plog(() => `Uncaught Exception: ${error.stack || error.message}`);
  };
  process.on('unhandledRejection', onRejection);
  process.on('uncaughtException', onException);

  const modelsPayload = JSON.stringify(
    formatAnthropicModelList(
      routes.map(r => ({ id: r.aliasId, name: r.displayName, contextWindow: r.contextWindow })),
    ),
  );

  const server = createServer(async (req, res) => {
    plog(() => `${req.method} ${req.url}`);

    // HEAD / — health check ping from Claude Code
    if (req.method === 'HEAD') {
      res.writeHead(200);
      res.end();
      return;
    }

    // GET /v1/models — Claude Code validates the model on startup and populates /model picker
    if (req.method === 'GET' && req.url?.startsWith('/v1/models')) {
      const modelPathMatch = req.url.match(/^\/v1\/models\/([^?]+)/);
      if (modelPathMatch) {
        const id = decodeURIComponent(modelPathMatch[1]);
        const route = lookupRoute(byAlias, id);
        if (route) {
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify(formatAnthropicModelEntry(route.aliasId, route.displayName, route.contextWindow)));
        } else {
          res.writeHead(404, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: { type: 'not_found_error', message: `Model '${id}' not found` } }));
        }
      } else {
        res.writeHead(200, { 'Content-Type': 'application/json' });
        res.end(modelsPayload);
      }
      return;
    }

    const messagesEndpoint = anthropicMessagesEndpoint(req.url);

    // Anthropic message creation and token counting are distinct endpoints.
    if (req.method === 'POST' && messagesEndpoint) {
      const inboundKey = extractApiKey(req);
      if (inboundKey !== proxyToken) {
        anthropicError(res, 401, 'Invalid proxy token');
        return;
      }

      const clientAbort = watchClientDisconnect(res);

      let anthropicBody: any;
      try {
        const raw = await readBody(req);
        anthropicBody = JSON.parse(raw);
      } catch {
        anthropicError(res, 400, 'Invalid JSON body');
        return;
      }

      const originalModel = anthropicBody.model;
      const clientWantsStream = Boolean(anthropicBody.stream);
      const relayRequestIdRaw = req.headers['x-relay-request-id'];
      const relayRequestId = Array.isArray(relayRequestIdRaw) ? relayRequestIdRaw[0] : relayRequestIdRaw;

      // Per-request route resolution: look up the alias, fall back to default
      const resolvedRoute = typeof originalModel === 'string'
        ? lookupRoute(byAlias, originalModel)
        : undefined;
      const configuredModelUnavailable = typeof originalModel === 'string'
        && (
          normalizeRouteLookupId(originalModel).startsWith('clodex:')
          || configuredAliasNames.has(normalizeRouteLookupId(originalModel))
        );
      if (!resolvedRoute && configuredModelUnavailable) {
        anthropicError(
          res,
          400,
          routeUnavailableMessage(
            originalModel,
            unavailableAliasReasons.get(normalizeRouteLookupId(originalModel)),
          ),
        );
        return;
      }
      const route = resolvedRoute ?? defaultRoute;
      // The identity a translated response reports. A request that named a
      // route we honoured keeps its public id: substituting the canonical id broke
      // auto-compaction for patched/alias ids in the field. (The window lookup does
      // not read the response body in 2.1.261 — see
      // `.claude/docs/claude-code-internals.md` — so keep this for identity
      // consistency, not because the client parses it.)
      // On a default-route fallback, report the answering/default model rather
      // than the unresolved requested id. Built-in Anthropic-format fallback is
      // routine in this PR, so the translated path must make that identity explicit.
      const responseModelId = resolvedRoute && typeof originalModel === 'string'
        ? originalModel
        : route.realModelId;
      // Anthropic-format is not the same question as "implements
      // count_tokens". Before third-party anthropic-format routes existed the
      // local path was the only one ever taken; now a route whose upstream
      // documents no token-counting endpoint would forward the count and
      // answer Claude Code's token accounting with a 404. Only an explicit
      // `false` diverts — an unset capability keeps forwarding, so a custom
      // Anthropic-compatible endpoint that does implement it is unaffected.
      if (
        messagesEndpoint === 'count_tokens'
        && (route.modelFormat !== 'anthropic'
          || route.compatibility?.supportsCountTokens === false)
      ) {
        const inputTokens = estimateAnthropicInputTokens(anthropicBody);
        plog(() => `token-count: local estimate model=${originalModel} input_tokens=${inputTokens}`);
        res.setHeader('x-relay-token-count-source', 'local-estimate');
        sendJson(res, 200, { input_tokens: inputTokens });
        return;
      }

      let apiKey = route.apiKey;
      if (route.authType === 'oauth' && route.refreshToken) {
        try {
          const current = await route.refreshToken();
          if (!current) throw new Error('credential is missing');
          apiKey = current;
          route.apiKey = current;
        } catch (err) {
          plog(() =>
            `oauth credential unavailable: ${err instanceof Error ? err.message : String(err)}`,
          );
          anthropicError(res, 401, 'OAuth credential is unavailable');
          return;
        }
      }
      const upstreamUrl = route.upstreamUrl;
      const routeAuthType = route.authType ?? 'api';

      // Record the tier clodex resolved for this route before dispatch. This is
      // requested intent; the provider SDK may still omit it during serialization.
      const loggedTier = isOpenAiOAuthRoute(route) ? oauthServiceTier() : undefined;
      plog(() =>
        `POST /v1/messages - alias=${originalModel} route=${route.realModelId} format=${route.modelFormat} key=${routeAuthType === 'none' ? 'none' : apiKey ? `len:${apiKey.length}` : 'MISSING'}`
        + (loggedTier ? ` tier=${loggedTier}` : ''),
      );

      const usesSdkAdapter = isSdkMigratedNpm(route.npm);

      if (messagesEndpoint === 'count_tokens') {
        if (!apiKey && routeAuthType !== 'none') {
          anthropicError(res, 401, 'Missing API key');
          return;
        }

        const betaHeaderRaw = req.headers['anthropic-beta'];
        const inboundBeta = Array.isArray(betaHeaderRaw) ? betaHeaderRaw.join(',') : betaHeaderRaw;
        const forwardBody = { ...anthropicBody, model: route.realModelId };
        const targetUrl = `${upstreamUrl}/v1/messages/count_tokens`;
        const isOAuth = routeAuthType === 'oauth';
        try {
          await relayAnthropicMessages(res, targetUrl, forwardBody, apiKey, false, {
            inboundBeta,
            authType: routeAuthType,
            log: message => plog(message),
            extraHeaders: route.headers,
            refreshToken: route.refreshToken,
            onTokenRefreshed: refreshed => { route.apiKey = refreshed; },
            signal: clientAbort.signal,
          });
        } catch (err) {
          if (clientDisconnected(clientAbort.signal)) return;
          const message = err instanceof UpstreamUnreachableError ? err.message : String(err);
          plog(() => `anthropic token-count error: ${message}`);
          anthropicError(res, 502, message);
        }
        return;
      }

      // A thread continuation carries only the messages after Claude Code's
      // anchor. Only Anthropic's own API, reached by raw passthrough, holds the
      // rest; any other upstream, and any translated route (the SDK does not
      // send `thread`), would answer the fragment as if it were the whole
      // conversation. Refuse it here, before any format branch, with the code
      // Claude Code answers by resending the full history.
      const holdsThreads = route.modelFormat === 'anthropic' && upstreamHoldsThreads(upstreamUrl);
      if (!holdsThreads && isThreadContinuation(anthropicBody)) {
        plog(() => `thread continuation refused: route=${route.realModelId} upstream does not hold threads`);
        res.writeHead(400, { 'Content-Type': 'application/json' });
        res.end(THREAD_UNSUPPORTED_BODY);
        return;
      }

      if (!apiKey && routeAuthType !== 'none' && !usesSdkAdapter) {
        anthropicError(res, 401, 'Missing API key');
        return;
      }

      // ── Anthropic passthrough ───────────────────────────────────────
      // Forward raw Anthropic body (with real model id) directly to the upstream.
      // No translation needed — the upstream speaks Anthropic natively.
      if (route.modelFormat === 'anthropic') {
        const betaHeaderRaw = req.headers['anthropic-beta'];
        const inboundBeta = Array.isArray(betaHeaderRaw) ? betaHeaderRaw.join(',') : betaHeaderRaw;
        const forwardBody = { ...anthropicBody, model: route.realModelId };
        const targetUrl = `${upstreamUrl}/v1/messages`;
        const isOAuth = routeAuthType === 'oauth';

        let effectiveBeta = inboundBeta;
        let claudeCodeSessionId: string | undefined;
        const inboundSessionRaw = req.headers['x-claude-code-session-id'];
        const goSessionHeaders = openCodeGoSessionHeaders(
          { providerId: route.providerId, baseUrl: upstreamUrl },
          extractClaudeSessionId(
            anthropicBody,
            Array.isArray(inboundSessionRaw) ? inboundSessionRaw[0] : inboundSessionRaw,
          ),
        );
        if (isOAuth) {
          // Identity injection and beta selection for Claude Code OAuth.
          const seed = route.providerId ?? route.realModelId;
          const identity = injectClaudeIdentity(forwardBody, route.providerData, seed);
          if (route.providerId === 'claude-code') injectClaudeCodeBillingSystemLine(forwardBody);
          claudeCodeSessionId = identity.sessionId;
          effectiveBeta = selectBetaFlags(forwardBody, route.realModelId, inboundBeta);
          plog(() => `anthropic-oauth: model=${route.realModelId}, beta=${effectiveBeta}`);
          plog(() => `anthropic-oauth headers: user-agent=claude-cli/${CLAUDE_CODE_CLI_VERSION} x-app=cli session-header=${claudeCodeSessionId ? 'set' : 'missing'}`);
        } else {
          plog(() => `anthropic-passthrough: model=${route.realModelId}, stream=${clientWantsStream}`);
        }

        try {
          await relayAnthropicMessages(res, targetUrl, forwardBody, apiKey, clientWantsStream, {
            inboundBeta: effectiveBeta,
            authType: routeAuthType,
            log: message => plog(message),
            claudeCodeSessionId,
            extraHeaders: { ...route.headers, ...goSessionHeaders },
            refreshToken: route.refreshToken,
            onTokenRefreshed: refreshed => { route.apiKey = refreshed; },
            signal: clientAbort.signal,
            // A route selected through a clodex: id or short alias must echo the
            // exact requested id back, or patched Claude Code misses the alias
            // context-window key and can skip auto-compaction.
            //
            // Only when the alias actually resolved. On the default-route
            // fallback the requested id names no route we honoured, so echoing
            // it would report the request's identity as the answering model —
            // the client would believe it reached a model it never reached, and
            // would key its context window off that phantom id.
            responseModelOverride:
              resolvedRoute
                && typeof originalModel === 'string'
                && originalModel !== route.realModelId
                ? originalModel
                : undefined,
            // An upstream that holds no threads must not hand Claude Code an id
            // it would anchor the next request on.
            anchorSafeMessageIds: !holdsThreads,
            onUpstreamError: inferenceLogPath
              ? (statusCode, errorContent) => writeInferenceResponseErrorLog(inferenceLogPath, {
                  modelId: originalModel,
                  provider: route.providerId ?? route.aliasId.split(':')[1] ?? 'unknown',
                  route: 'passthrough',
                  statusCode,
                  errorContent,
                })
              : undefined,
          });
        } catch (err) {
          if (clientDisconnected(clientAbort.signal)) return;
          const message = err instanceof UpstreamUnreachableError ? err.message : String(err);
          plog(() => `anthropic-passthrough error: ${message}`);
          anthropicError(res, 502, message);
        }
        return;
      }

      // ── SDK-backed providers (Vercel AI SDK) ────────────────────────
      // OpenCode-assigned npm packages route through the SDK, which owns wire
      // format, endpoint selection, and provider quirks.
      if (usesSdkAdapter) {
        const openAiOAuth = isOpenAiOAuthRoute(route);
        const claudeSessionIdHeader = Array.isArray(req.headers['x-claude-code-session-id'])
          ? req.headers['x-claude-code-session-id'][0]
          : req.headers['x-claude-code-session-id'];
        const claudeSessionId = extractClaudeSessionId(anthropicBody, claudeSessionIdHeader);
        const claudeAgentIds = extractClaudeAgentIds(req.headers);
        const translationLifecycle = createTranslationLifecycle(
          inferenceLogPath,
          relayRequestId,
          claudeSessionId,
          originalModel,
          route.providerId ?? route.aliasId.split(':')[1] ?? 'unknown',
        );
        const runSdkRequest = async (): Promise<void> => {
          const params = sdkTranslateRequest(anthropicBody, route.npm!, {
            openAiOAuth,
            claudeSessionId,
            maxTools: maxToolsForNpm(route.npm),
            log: plog,
            reasoningMetadata: {
              providerId: route.providerId,
              apiBaseUrl: route.baseURL,
              supportedParameters: route.supportedParameters,
              reasoning: route.reasoning,
              interleavedReasoningField: route.interleavedReasoningField,
              compatibility: route.compatibility,
              upstreamModelId: route.realModelId,
            },
          });
          const goSdkSessionHeaders = openCodeGoSessionHeaders(
            { providerId: route.providerId, baseUrl: route.baseURL },
            claudeSessionId,
          );
          if (goSdkSessionHeaders) params.headers = { ...params.headers, ...goSdkSessionHeaders };
          plog(() =>
            `sdk: npm=${route.npm} model=${route.realModelId}, stream=${clientWantsStream}, ` +
            `tools=${anthropicBody.tools?.length ?? 0}, msgs=${params.messages.length}`,
          );
          const model = await createLanguageModel({
            npm: route.npm!,
            modelId: route.realModelId,
            apiKey,
            baseURL: route.baseURL,
            providerId: route.providerId ?? route.aliasId,
            authType: route.authType,
            oauthAccountId: route.oauthAccountId,
            providerData: route.providerData,
            headers: route.headers,
            useResponsesLite: route.useResponsesLite,
            preferWebSockets: route.preferWebSockets,
            compatibility: route.compatibility,
            onDebug: (msg: string) => plog(() => msg),
            onWebSocketDiagnostic: webSocketDiagnosticsLogPath
              ? event => writeWebSocketDiagnosticLog(webSocketDiagnosticsLogPath, event)
              : undefined,
          });
          translationLifecycle?.dispatched();
          if (clientWantsStream) {
            // Internal override (primarily a test seam / operational tuning knob).
            const keepAliveMs =
              Number(process.env.CLODEX_STREAM_KEEPALIVE_INTERVAL_MS) || STREAM_KEEPALIVE_INTERVAL_MS;
            let lastDownstreamWriteAt = Date.now();
            let lastUpstreamPartAt = Date.now();
            const writeStreamChunk = (chunk: string) => {
              translationLifecycle?.onOutput(chunk);
              if (!res.headersSent) {
                res.writeHead(200, {
                  'Content-Type': 'text/event-stream',
                  'Cache-Control': 'no-cache',
                  'Connection': 'keep-alive',
                });
              }
              lastDownstreamWriteAt = Date.now();
              res.write(chunk);
            };
            // Heartbeat: while upstream keeps delivering parts but nothing has
            // been written downstream for a full interval (a large tool-call
            // argument being buffered), emit a ping so Claude Code's ~180s
            // read-idle timeout does not fire mid-argument. Deliberately does
            // NOT go through translationLifecycle.onOutput so diagnostic
            // outputIdleMs still reflects the real buffering gap.
            const keepAlive = setInterval(() => {
              if (res.writableEnded || !res.headersSent) return;
              const now = Date.now();
              const outputIdleMs = now - lastDownstreamWriteAt;
              const upstreamIdleMs = now - lastUpstreamPartAt;
              if (outputIdleMs >= keepAliveMs && upstreamIdleMs < keepAliveMs) {
                lastDownstreamWriteAt = now;
                res.write(STREAM_KEEPALIVE_PING);
                plog(() => `stream keepalive ping: output idle ${outputIdleMs}ms, upstream active (${upstreamIdleMs}ms since last part)`);
              }
            }, keepAliveMs);
            keepAlive.unref();
            try {
              await withResponsesWebSocketDiagnosticContext(
                { requestId: relayRequestId, claudeSessionId, ...claudeAgentIds },
                () => streamAnthropicResponse(
                  model,
                  params,
                  responseModelId,
                  writeStreamChunk,
                  plog,
                  {
                    onPart: partType => {
                      lastUpstreamPartAt = Date.now();
                      translationLifecycle?.onPart(partType);
                    },
                    onPromptTokens: total => reportPricingBoundaryCrossing({
                      modelKey: route.aliasId,
                      modelLabel: route.displayName || route.aliasId,
                      pricingBoundary: route.pricingBoundary,
                      inputTokens: total,
                    }),
                    initialInputTokens: estimateAnthropicInputTokens(anthropicBody),
                    abortSignal: clientAbort.signal,
                  },
                ),
              );
            } finally {
              clearInterval(keepAlive);
            }
            translationLifecycle?.complete();
            if (!res.headersSent) writeStreamChunk('');
            res.end();
          } else {
            // ChatGPT's Codex backend (OpenAI OAuth) rejects non-streaming requests
            // outright ("Stream must be set to true"), so always stream internally
            // for it and collect the result, regardless of what the client asked for.
            const anthropicResponse = await withResponsesWebSocketDiagnosticContext(
              { requestId: relayRequestId, claudeSessionId, ...claudeAgentIds },
              () => generateAnthropicResponse(
                model,
                params,
                responseModelId,
                {
                  forceStream: openAiOAuth,
                  abortSignal: clientAbort.signal,
                  onPart: partType => translationLifecycle?.onPart(partType),
                  onPromptTokens: total => reportPricingBoundaryCrossing({
                    modelKey: route.aliasId,
                    modelLabel: route.displayName || route.aliasId,
                    pricingBoundary: route.pricingBoundary,
                    inputTokens: total,
                  }),
                },
              ),
            );
            translationLifecycle?.onOutput(JSON.stringify(anthropicResponse));
            translationLifecycle?.complete();
            sendJson(res, 200, anthropicResponse);
          }
        };

        let sdkAttempt = 0;
        const handleSdkError = async (
          err: unknown,
        ): Promise<'retry' | 'cancelled' | 'done'> => {
          if (clientDisconnected(clientAbort.signal)) {
            translationLifecycle?.cancel();
            return 'cancelled';
          }
          const message = formatUpstreamError(err);
          const details = sdkUpstreamErrorDetails(err);
          const upstreamStatus = details?.statusCode ?? upstreamHttpStatus(err, message);
          const replacement = await resolveOAuthRetryReplacement(
            openAiOAuth,
            upstreamStatus,
            sdkAttempt,
            res.headersSent,
            apiKey,
            route.refreshToken,
          );
          if (replacement) {
            apiKey = replacement;
            route.apiKey = replacement;
            sdkAttempt += 1;
            plog(() => 'sdk oauth credential replaced after 401; retrying once');
            return 'retry';
          }
          translationLifecycle?.fail(
            err instanceof Error ? err.name : 'UpstreamError',
            sdkTranslationErrorSignature(err),
            details?.transportCode,
          );
          const contextLengthExceeded = upstreamStatus === 400
            && isContextLengthExceededError(err, message);
          const clientMessage = contextLengthExceeded
            ? anthropicPromptTooLongMessage(
                anthropicBody,
                resolveContextWindow(route.realModelId, route.contextWindow),
              )
            : message;
          plog(() => `sdk error: ${message}${details?.errorContent ? ` — body: ${details.errorContent}` : ''}`);
          if (inferenceLogPath && upstreamStatus >= 400) {
            writeInferenceResponseErrorLog(inferenceLogPath, {
              ...(relayRequestId ? { requestId: relayRequestId } : {}),
              modelId: originalModel,
              provider: route.providerId ?? route.aliasId.split(':')[1] ?? 'unknown',
              route: 'translated',
              statusCode: upstreamStatus,
              errorContent: details?.errorContent ?? message,
              isRetryable: details?.isRetryable,
              attemptCount: details?.attemptCount,
            });
          }
          if (!res.headersSent) {
            if (details?.retryAfterSeconds !== undefined) {
              res.setHeader('retry-after', String(details.retryAfterSeconds));
            }
            anthropicError(
              res,
              upstreamStatus === 500 ? 502 : upstreamStatus,
              clientMessage,
              contextLengthExceeded ? (relayRequestId ?? randomUUID()) : undefined,
            );
          } else {
            const errorType = anthropicErrorType(upstreamStatus, details?.transportCode);
            res.write(`event: error\ndata: ${JSON.stringify({
              type: 'error',
              error: { type: errorType, message: clientMessage },
              ...(contextLengthExceeded ? { request_id: relayRequestId ?? randomUUID() } : {}),
            })}\n\n`);
            res.end();
          }
          return 'done';
        };

        for (;;) {
          try {
            await runSdkRequest();
            break;
          } catch (err) {
            const outcome = await handleSdkError(err);
            if (outcome === 'retry') continue;
            if (outcome === 'cancelled') return;
            break;
          }
        }
        return;
      }

      // Non-anthropic route without a registered SDK npm — misconfigured route.
      anthropicError(res, 500, `No SDK provider configured for model ${originalModel} (npm=${route.npm ?? 'none'})`);
      return;
    }

    // Everything else → 404
    anthropicError(res, 404, `Unknown endpoint: ${req.method} ${req.url}`);
  });
  server.keepAliveTimeout = INTERNAL_ADAPTER_KEEPALIVE_TIMEOUT_MS;

  let address: AddressInfo;
  try {
    address = await listenTcpServer(server, 0, '127.0.0.1');
  } catch (error) {
    process.off('unhandledRejection', onRejection);
    process.off('uncaughtException', onException);
    throw error;
  }
  plog(() =>
    `started on port ${address.port}, catalog=${routes.length} model(s), default=${defaultRoute.aliasId}`,
  );
  return {
    port: address.port,
    token: proxyToken,
    close: () => {
      process.off('unhandledRejection', onRejection);
      process.off('uncaughtException', onException);
      server.close();
    },
  };
}

/** Per-model metadata the single-model wrapper carries onto its route. */
export interface SingleModelProxyOptions {
  npm?: string;
  baseURL?: string;
  upstreamModelId?: string;
  providerId?: string;
  authType?: 'api' | 'oauth' | 'none';
  oauthAccountId?: string;
  providerData?: Record<string, unknown>;
  modelFormat?: 'anthropic' | 'openai';
  supportedParameters?: string[];
  reasoning?: boolean;
  interleavedReasoningField?: string;
  useResponsesLite?: boolean;
  preferWebSockets?: boolean;
  compatibility?: ModelRuntimeCompatibility;
  headers?: Record<string, string>;
  /** Input size above which the provider bills the whole request at a higher rate. */
  pricingBoundary?: number;
}

/**
 * The route the single-model wrapper hands to the catalog. Exported because this
 * literal is where a field silently stops being carried: it has no spread, so a
 * property omitted here reaches the runtime as `undefined` with nothing failing.
 */
export function singleModelProxyRoute(
  completionsUrl: string,
  modelId: string,
  contextWindow?: number,
  sdk?: SingleModelProxyOptions,
  apiKey?: string,
): ProxyRoute {
  const bareModelId = stripOneMContextSuffix(modelId);
  return {
    aliasId: claudeCodeClientModelId(modelId, contextWindow),
    realModelId: sdk?.upstreamModelId ?? bareModelId,
    displayName: bareModelId,
    upstreamUrl: completionsUrl,
    apiKey: apiKey ?? '',
    modelFormat: sdk?.modelFormat ?? 'openai',
    contextWindow,
    pricingBoundary: sdk?.pricingBoundary,
    npm: sdk?.npm,
    baseURL: sdk?.baseURL,
    providerId: sdk?.providerId,
    authType: sdk?.authType,
    oauthAccountId: sdk?.oauthAccountId,
    providerData: sdk?.providerData,
    supportedParameters: sdk?.supportedParameters,
    reasoning: sdk?.reasoning,
    interleavedReasoningField: sdk?.interleavedReasoningField,
    useResponsesLite: sdk?.useResponsesLite,
    preferWebSockets: sdk?.preferWebSockets,
    compatibility: sdk?.compatibility,
    headers: sdk?.headers,
  };
}

export function startProxy(
  completionsUrl: string,
  modelId: string,
  debug = false,
  contextWindow?: number,
  sdk?: SingleModelProxyOptions,
  apiKey?: string,
): Promise<ProxyHandle> {
  const route = singleModelProxyRoute(completionsUrl, modelId, contextWindow, sdk, apiKey);
  return startProxyCatalog([route], route.aliasId, debug);
}
